import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import {cva} from 'platform/shared-tw';

import {useState, type ReactNode} from 'react';

import {isNil} from 'ramda';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  readonly children: ReactNode;
  readonly content: ReactNode;
  readonly placement?: TooltipPlacement;
  readonly openDelay?: number;
  readonly closeDelay?: number;
  /**
   * Make the wrapper itself focusable (`tabIndex=0`). Off by default to avoid
   * adding a tab stop per badge in long lists. Turn on when the children are
   * not natively focusable AND keyboard discoverability matters.
   */
  readonly isFocusable?: boolean;
}

const DEFAULT_OPEN_DELAY = 200;
const DEFAULT_CLOSE_DELAY = 100;
const TOOLTIP_OFFSET = 8;
const SHIFT_PADDING = 8;

const FLOATING_MIDDLEWARE = [offset(TOOLTIP_OFFSET), flip(), shift({padding: SHIFT_PADDING})];

const tooltipVariants = cva(
  'z-50 max-w-xs rounded-md bg-(--color-mira-gray-100) px-3 py-2 text-xs leading-snug text-white shadow-lg'
);

export function Tooltip(props: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);

  const {refs, floatingStyles, context} = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: props.placement ?? 'top',
    middleware: FLOATING_MIDDLEWARE,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    move: false,
    delay: {
      open: props.openDelay ?? DEFAULT_OPEN_DELAY,
      close: props.closeDelay ?? DEFAULT_CLOSE_DELAY,
    },
    handleClose: safePolygon(),
  });
  const focus = useFocus(context);
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {role: 'tooltip'});

  const {getReferenceProps, getFloatingProps} = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ]);

  if (isNil(props.content) || props.content === '') {
    return <>{props.children}</>;
  }

  return (
    <>
      <span
        ref={refs.setReference}
        tabIndex={props.isFocusable ? 0 : undefined}
        className="inline-flex"
        {...getReferenceProps()}
      >
        {props.children}
      </span>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={tooltipVariants()}
          >
            {props.content}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
